import { BrandMark } from "@healthos/ui";
import { ArrowClockwise, Keyhole } from "@phosphor-icons/react";

type Props = {
  recheckStatus: "idle" | "denied" | "refreshFailed";
  rechecking: boolean;
  onRecheck: () => void;
  onGoToOperations?: () => void;
  variant?: "healthos" | "product" | "lookup";
};

function accessDeniedCopy(variant: Props["variant"]) {
  if (variant === "lookup") {
    return {
      title: "We could not confirm your access.",
      lede: "HealthOS could not check which products this account can use. Recheck in a moment.",
      next: "If this keeps happening, contact your HealthOS administrator.",
    };
  }
  if (variant === "product") {
    return {
      title: (
        <>
          You don&apos;t have access to this app <em>yet.</em>
        </>
      ),
      lede: "Your account is signed in, but Companion or Contact Centre still needs to be assigned before you can continue.",
      next: "Please contact your HealthOS administrator to request access. Once it has been assigned, recheck here to refresh your session.",
    };
  }
  return {
    title: (
      <>
        You haven&apos;t been assigned access to <em>HealthOS.</em>
      </>
    ),
    lede: "Your account is signed in, but access still needs to be granted before you can begin.",
    next: "Please contact your HealthOS administrator to request access. Once it has been assigned, recheck here to refresh your session.",
  };
}

export function ScreenAccessDenied({
  recheckStatus,
  rechecking,
  onRecheck,
  onGoToOperations,
  variant = "healthos",
}: Props) {
  const isProduct = variant === "product";
  const copy = accessDeniedCopy(variant);
  const statusMessage =
    recheckStatus === "refreshFailed"
      ? "We could not refresh your access. Try again, or contact your administrator."
      : recheckStatus === "denied"
        ? isProduct
          ? "Companion and Contact Centre have not been assigned to this account yet."
          : "Access has not been assigned to this account yet."
        : null;

  return (
    <div className="ob-page">
      <header className="ob-header access-denied-header">
        <div className="ob-brand">
          <BrandMark size="nav" />
          <span>HealthOS</span>
        </div>
      </header>

      <main className="ob-stage access-denied-stage">
        <section className="ob-screen access-denied-screen" aria-labelledby="access-denied-title">
          <div className="access-denied-mark" aria-hidden="true">
            <Keyhole size={24} weight="duotone" />
          </div>
          <p className="eyebrow">
            <span className="bar" /> HealthOS access
          </p>
          <h1 className="h1" id="access-denied-title">
            {copy.title}
          </h1>
          <p className="lede">{copy.lede}</p>
          <div className="access-denied-next-step">
            <span className="access-denied-step" aria-hidden="true">
              01
            </span>
            <div>
              <p className="access-denied-next-label">Next step</p>
              <p className="access-denied-next-copy">{copy.next}</p>
            </div>
          </div>
          <div className="access-denied-actions">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={onRecheck}
              disabled={rechecking}
            >
              <ArrowClockwise size={17} weight="bold" aria-hidden="true" />
              {rechecking ? "Checking access" : "Recheck access"}
            </button>
            {onGoToOperations ? (
              <button type="button" className="btn btn-ghost btn-large" onClick={onGoToOperations}>
                Go to Operations
              </button>
            ) : null}
            {rechecking ? (
              <p className="access-denied-status" role="status" aria-live="polite">
                Refreshing your session and checking access.
              </p>
            ) : null}
            {statusMessage ? (
              <p className="access-denied-status" role="status" aria-live="polite">
                {statusMessage}
              </p>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
